import React, { useState, useEffect } from 'react';
import { FiLink, FiCheck, FiX, FiRotateCcw, FiAlertCircle } from 'react-icons/fi';

const MatchingQuestion = ({ 
  question, 
  onAnswer, 
  currentAnswer = null,
  isSubmitted = false,
  timeSpent = 0 
}) => {

  const [matches, setMatches] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Initialize matches from current answer or empty state
  useEffect(() => {
    if (currentAnswer && currentAnswer.matches) {
      setMatches(currentAnswer.matches);
    } else {
      // Initialize empty matches
      const initialMatches = {};
      if (question.options) {
        // Group options by pairId to create left and right columns
        const leftColumnOptions = question.options.filter(opt => {
          const id = opt.pairId !== undefined ? opt.pairId : opt.sortOrder;
          return id % 2 === 0;
        }).sort((a, b) => {
          const idA = a.pairId !== undefined ? a.pairId : a.sortOrder;
          const idB = b.pairId !== undefined ? b.pairId : b.sortOrder;
          return idA - idB;
        });
        leftColumnOptions.forEach(option => {
          initialMatches[option.id] = '';
        });
      }
      setMatches(initialMatches);
    }
  }, [currentAnswer, question.options]);

  // Separate options into left and right columns
  // For matching questions, we need to pair items correctly
  // Left column: items with even pairId (0, 2, 4, ...)
  // Right column: items with odd pairId (1, 3, 5, ...)
  const leftColumnOptions = question.options?.filter(opt => {
    const id = opt.pairId !== undefined ? opt.pairId : opt.sortOrder;
    return id % 2 === 0;
  }).sort((a, b) => {
    const idA = a.pairId !== undefined ? a.pairId : a.sortOrder;
    const idB = b.pairId !== undefined ? b.pairId : b.sortOrder;
    return idA - idB;
  }) || [];
  
  const rightColumnOptions = question.options?.filter(opt => {
    const id = opt.pairId !== undefined ? opt.pairId : opt.sortOrder;
    return id % 2 === 1;
  }).sort((a, b) => {
    const idA = a.pairId !== undefined ? a.pairId : a.sortOrder;
    const idB = b.pairId !== undefined ? b.pairId : b.sortOrder;
    return idA - idB;
  }) || [];
  
  // Alternative approach: if the above doesn't work, try using the first half as left, second half as right
  if (leftColumnOptions.length === 0 || rightColumnOptions.length === 0) {
    const midPoint = Math.ceil(question.options.length / 2);
    const altLeftColumnOptions = question.options.slice(0, midPoint);
    const altRightColumnOptions = question.options.slice(midPoint);
    
    if (leftColumnOptions.length === 0) leftColumnOptions.push(...altLeftColumnOptions);
    if (rightColumnOptions.length === 0) rightColumnOptions.push(...altRightColumnOptions);
  }

  const handleMatchChange = (leftOptionId, rightOptionId) => {
    if (isSubmitted) return; // Prevent changes after submission

    setMatches(prev => ({
      ...prev,
      [leftOptionId]: rightOptionId
    }));
  };

  const clearMatches = () => {
    if (isSubmitted) return;
    
    const clearedMatches = {};
    leftColumnOptions.forEach(option => {
      clearedMatches[option.id] = '';
    });
    setMatches(clearedMatches);
  };

  const validateMatches = () => {
    const matchedCount = Object.values(matches).filter(match => match !== '').length;
    if (matchedCount === 0) {
      alert('Please match at least one item before submitting.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validateMatches()) return;

    setIsSubmitting(true);
    
    try {
      await onAnswer({
        questionId: question.id,
        matches,
        type: 'MATCHING',
        timeSpent
      });
      
      setShowFeedback(true);
    } catch (error) {
      console.error('Error submitting matching answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMatchStatus = (leftOptionId) => {
    if (!showFeedback || !currentAnswer) return null;
    
    const userMatch = matches[leftOptionId];
    const leftOption = question.options.find(opt => opt.id === leftOptionId);
    const correctMatch = leftOption ? question.options.find(opt => {
      const leftId = leftOption.pairId !== undefined ? leftOption.pairId : leftOption.sortOrder;
      const rightId = opt.pairId !== undefined ? opt.pairId : opt.sortOrder;
      return rightId === leftId + 1;
    })?.id : null;
    
    if (userMatch === correctMatch) {
      return 'correct';
    } else if (userMatch !== '') {
      return 'incorrect';
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 bg-green-50';
      case 'incorrect': return 'border-red-500 bg-red-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <FiCheck className="text-green-600 text-lg" />;
      case 'incorrect': return <FiX className="text-red-600 text-lg" />;
      default: return null;
    }
  };

  // Handle case where there are no options yet
  if (!question.options || question.options.length === 0 || leftColumnOptions.length === 0 || rightColumnOptions.length === 0) {
    return (
      <div className="matching-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
        <div className="mb-6">
          {/* Question Text */}
          <div className="text-black text-base font-bold leading-relaxed mb-4">
            {question.text}
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-800">
              <FiAlertCircle className="text-lg" />
              <span className="font-medium">Question Preview</span>
            </div>
            <p className="text-gray-700 mt-2">
              This is a matching question. The question text is displayed above, but the matching pairs are not yet configured. 
              When fully configured, you'll see two columns of items to match.
              <br /><br />
              <strong>Debug Info:</strong> Options: {question.options?.length || 0}, 
              Left Column: {leftColumnOptions.length}, 
              Right Column: {rightColumnOptions.length}
            </p>
            <div className="mt-3 p-3 bg-white rounded border">
              <p className="text-sm text-gray-600">
                <strong>Question:</strong> {question.text}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="matching-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
      {/* Question Text */}
      <div className="mb-6">
        <div className="text-black text-base font-bold leading-relaxed mb-6">
          {question.text}
        </div>


      </div>

      {/* Images if any */}
      {question.images && question.images.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Supporting Materials:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {question.images.map((image, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <img 
                  src={image.imageUrl} 
                  alt={image.altText || `Question image ${index + 1}`}
                  className="w-full h-auto max-h-64 object-contain"
                />
                {image.altText && (
                  <div className="p-3 bg-gray-50 text-sm text-gray-600">
                    {image.altText}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matching Interface */}
      <div className="mb-4 sm:mb-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-gray-800 text-center pb-2 border-b-2 border-gray-300">
              Column A
            </h4>
            {leftColumnOptions.map((option, index) => (
              <div 
                key={option.id}
                className={`p-3 sm:p-4 border-2 rounded-lg transition-all duration-200 ${
                  getStatusColor(getMatchStatus(option.id))
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm font-medium text-gray-600">Item {index + 1}</span>
                  {getStatusIcon(getMatchStatus(option.id))}
                </div>
                <div className="text-gray-800 text-sm sm:text-base font-medium mb-3 break-words">
                  {option.text || 'NO TEXT FOUND'}
                </div>
                
                {/* Dropdown for matching */}
                <select
                  value={matches[option.id] || ''}
                  onChange={(e) => handleMatchChange(option.id, e.target.value)}
                  disabled={isSubmitted}
                  className={`w-full p-2 border rounded-md text-xs sm:text-sm ${
                    isSubmitted 
                      ? 'bg-gray-100 cursor-not-allowed' 
                      : 'border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-gray-200'
                  }`}
                >
                  <option value="">Select a match...</option>
                  {rightColumnOptions.map((rightOption) => (
                    <option key={rightOption.id} value={rightOption.id}>
                      {rightOption.text}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {/* Right Column */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-gray-800 text-center pb-2 border-b-2 border-gray-300">
              Column B
            </h4>
            {rightColumnOptions.map((option, index) => (
              <div 
                key={option.id}
                className="p-3 sm:p-4 border-2 border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="text-xs sm:text-sm font-medium text-gray-600 mb-2">
                  Item {String.fromCharCode(65 + index)} {/* A, B, C, etc. */}
                </div>
                <div className="text-gray-800 text-sm sm:text-base font-medium break-words">
                  {option.text || 'NO TEXT FOUND'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {!isSubmitted && (
        <div className="flex justify-center items-center">
          <button
            onClick={clearMatches}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiRotateCcw className="text-sm" />
            Clear All Matches
          </button>
        </div>
      )}

      {/* Feedback Display */}
      {showFeedback && currentAnswer && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Your Matches:</h4>
          <div className="space-y-2">
            {leftColumnOptions.map((option, index) => {
              const userMatch = matches[option.id];
              const matchedOption = rightColumnOptions.find(opt => opt.id === userMatch);
              const status = getMatchStatus(option.id);
              
              return (
                <div key={option.id} className="flex items-center gap-3 text-sm">
                  <span className="font-medium text-gray-700">Item {index + 1}:</span>
                  <span className="text-gray-600">{option.text}</span>
                  <span className="text-gray-500">→</span>
                  <span className={`font-medium ${
                    status === 'correct' ? 'text-green-600' : 
                    status === 'incorrect' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {matchedOption ? matchedOption.text : 'No match'}
                  </span>
                  {getStatusIcon(status)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="mt-4 text-sm text-gray-600">
        Matched: {Object.values(matches).filter(match => match !== '').length} / {leftColumnOptions.length}
      </div>
    </div>
  );
};

export default MatchingQuestion; 